import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import {
  useTour,
  SaveTourStrategy,
} from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/Tour';
import { translate } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/util';
import { remCalc } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common';
import { formatTourDataWithStepsViewed } from '@monorepo/shared/src/util/tour-data-utils';
import useTourStore from '@monorepo/shared/src/graph/state/query-hooks/use-tour';
import WithQuery from 'components/layout/WithQuery';
import MatmanHelperButton from 'components/common/Buttons/MatmanHelperButton';
import NextButton from '@monorepo/shared/src/components/common/Buttons/NextButton';
import RewardsButton from 'components/common/Buttons/RewardsButton';
import Arrow from 'components/common/Tour/Arrow';
import MessageCallOutBanner from 'components/common/Tour/MessageCallOutBanner';
import AssignmentsIcon from 'components/modules/Home/components/AssignmentsIcon';
import GreatAlphabetParadeIcon from 'components/modules/Home/components/GreatAlphabetParadeIcon';
import Home from 'components/modules/Home/components/Home';
import LetterBooksIcon from 'components/modules/Home/components/LetterBooksIcon';
import { TourArrowTypes, TourStyleTypes, ButtonSize, TourSteps, TourName } from 'types';
import OnboardingOverlay from './components/OnboardingOverlay';
import OnboardingButtons from './components/OnboardingButtons';
import { stepToStyleMapper } from '../utils/tour-style-utils';
import useUserStore from 'graph/state/query-hooks/use-user';

const tHome = (key: string) => translate('home', key);

const { REACT_APP_APP_NAME: appName } = process.env;
const LIGHT_ME_UP = 9999;
const SLIGHTLY_LESS_LIGHT = 9998;

const Onboarding: React.FC = () => {
  const navigate = useNavigate();
  const { nextButtonPosition } = useStyles();

  const [
    { isLastStep, loading, activeStep, activeStepIndex, nextButtonVisible },
    { setActiveTour, setNextStep, handleExitTour, refresh },
  ] = useTour();

  const [{ tourData, tourSteps, loading: tourLoading }, { handleSaveTourSetting }] = useTourStore(
    TourName.ONBOARDING,
    appName
  );

  const [user] = useUserStore();

  const activeStepText = activeStep?.text;
  const activeStepName = activeStep?.name;

  const saveStrategy = SaveTourStrategy.SAVE_ALL_STEPS_ON_COMPLETE;

  const stepToArrowMapper = {
    welcome: TourArrowTypes.CURVED,
    matManIcon: TourArrowTypes.STRAIGHT,
    letterBooks: TourArrowTypes.CURVED,
    assignments: TourArrowTypes.STRAIGHT,
    greatAlphabetParade: TourArrowTypes.CURVED,
    rewards: TourArrowTypes.STRAIGHT,
  };

  const ArrowAndBannerComponents = (
    <>
      <Arrow
        variant={stepToArrowMapper[activeStepName]}
        styleName={stepToStyleMapper(TourStyleTypes.ARROW, activeStepName)}
      />

      <MessageCallOutBanner
        bannerClass={stepToStyleMapper(TourStyleTypes.BANNER, activeStepName)}
        text={activeStepText}
        imageClass={stepToStyleMapper(TourStyleTypes.MATMANIMAGE, activeStepName)}
      />
    </>
  );

  const helperButtonStyles = {
    bottom: remCalc(24),
    position: 'fixed',
    left: remCalc(26),
  };

  const rewardsButtonStyles = {
    position: 'absolute',
    top: remCalc(24),
    right: remCalc(28),
  };

  const activeTileProps = {
    styles: {
      zIndex: SLIGHTLY_LESS_LIGHT,
      cursor: 'auto',
      ...(activeStepName === TourSteps.MAT_MAN_ICON && helperButtonStyles),
      ...(activeStepName === TourSteps.REWARDS && rewardsButtonStyles),
    },
    components: ArrowAndBannerComponents,
  };

  const handleRedirect = () => {
    navigate('/');
  };

  const saveTourSetting = async (isSingleStep: boolean) => {
    if (tourData?.id && user.me?.id && tourSteps) {
      const data = formatTourDataWithStepsViewed({
        isEducator: false,
        tourId: tourData?.id,
        userOrStudentId: user.me?.id,
        tourSteps,
        isSingleStep,
        activeStepIndex,
      });
      data && (await handleSaveTourSetting(data));
    }
  };

  const handleClose = async () => {
    handleExitTour();
    const isSingleStep = false;
    await saveTourSetting(isSingleStep);
    handleRedirect();
  };

  const handleNextClick = async () => {
    const isSingleStep = true;
    await saveTourSetting(isSingleStep);

    const isPrevious = false;
    setNextStep(isPrevious);

    if (isLastStep && !isPrevious) {
      handleClose();
    }
  };

  useEffect(() => {
    if (tourSteps) {
      refresh();
      setActiveTour(TourName.ONBOARDING, tourSteps, saveStrategy);
    }
  }, [tourSteps]);

  const NextButtonComponent = (
    <NextButton
      className={nextButtonPosition}
      onClick={handleNextClick}
      size={ButtonSize.SMALL}
      components={activeStepName === TourSteps.WELCOME && ArrowAndBannerComponents}
    />
  );

  const homeTiles = [
    {
      title: tHome(TourSteps.LETTER_BOOKS),
      Component: LetterBooksIcon,
      onClick: () => null,
      ...(activeStepName === TourSteps.LETTER_BOOKS && activeTileProps),
    },
    {
      title: tHome(TourSteps.ASSIGNMENTS),
      Component: AssignmentsIcon,
      onClick: () => null,
      ...(activeStepName === TourSteps.ASSIGNMENTS && activeTileProps),
    },
    {
      title: tHome(TourSteps.GREAT_ALPHABET_PARADE),
      Component: GreatAlphabetParadeIcon,
      onClick: () => null,
      ...(activeStepName === TourSteps.GREAT_ALPHABET_PARADE && activeTileProps),
    },
    {
      title: tHome(TourSteps.REWARDS),
      Component: RewardsButton,
      onClick: () => null,
      noRedirect: true,
      ...(activeStepName === TourSteps.REWARDS && activeTileProps),
    },
    {
      title: tHome(TourSteps.MAT_MAN_ICON),
      Component: MatmanHelperButton,
      onClick: () => null,
      noRedirect: true,
      ...(activeStepName === TourSteps.MAT_MAN_ICON && activeTileProps),
    },
  ];

  return (
    <WithQuery loading={loading || tourLoading}>
      <Box>
        <OnboardingOverlay />
        <OnboardingButtons
          onExitTourClick={handleClose}
          nextButton={NextButtonComponent}
          nextButtonVisible={nextButtonVisible}
        />
        <Home tiles={homeTiles} />
      </Box>
    </WithQuery>
  );
};

const useStyles = makeStyles((theme) => {
  return {
    nextButtonPosition: {
      zIndex: LIGHT_ME_UP,
      position: 'absolute',
      bottom: '30vh',
      right: 22,
      [theme.breakpoints.down('md')]: {
        bottom: 100,
      },
    },
  };
});

export default Onboarding;
